import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Typography from "@/components/ui/typography";
import {
  FoundersKeys,
  getFounderReferralCode,
} from "@/features/founders/api/founders";

interface ReferEarnDialogProps {
  children: React.ReactNode;
}

const ReferEarnDialog = ({ children }: ReferEarnDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [FoundersKeys.ReferralCode],
    queryFn: getFounderReferralCode,
    enabled: isOpen,
  });

  const referralLink = data?.referralCode
    ? `https://www.stack.expert/?utm_source=ref&as=${data.referralCode}`
    : "";

  const copyToClipboard = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied to clipboard!");

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link. Please try again.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-[80%] max-w-lg rounded-2xl">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            Refer & Earn
          </DialogTitle>
          <DialogDescription className="pt-2 text-base">
            Building is more fun with friends! Use your link below and earn{" "}
            <span className="font-semibold text-primary">$200</span> when your
            referral joins Stack.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <Typography size="l2" className="ml-2 text-gray-600">
                Loading your referral code...
              </Typography>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <Typography size="l2" className="text-red-600 mb-2">
                Failed to load referral code. Please try again.
              </Typography>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          ) : referralLink ? (
            <>
              <div
                onClick={copyToClipboard}
                className="p-4 bg-muted rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors group relative overflow-hidden"
              >
                <Typography
                  size="l2"
                  as="p"
                  className="font-mono break-all pr-10 text-gray-700"
                >
                  {referralLink}
                </Typography>
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard();
                  }}
                >
                  <Copy
                    className={`h-4 w-4 ${
                      copied ? "text-green-600" : "text-muted-foreground"
                    }`}
                  />
                </Button>
              </div>

              {copied && (
                <Typography size="l2" className="text-center text-green-600">
                  Link copied! Share it with your friends.
                </Typography>
              )}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferEarnDialog;
